import React from 'react';
import { motion } from 'framer-motion';
import { Upload, Wand2, Share2 } from 'lucide-react';
import { StepCard } from './steps/StepCard';
import { VideoComparison } from './steps/VideoComparison';
import { CharacterOption } from './steps/CharacterOption';
import { characters } from '../../data/characters';
import { Button } from '../ui/Button';

export const HowItWorks: React.FC = () => {
  return (
    <section id="how-it-works" className="py-24 bg-gradient-to-b from-white to-red-50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-64 h-64 bg-red-100 rounded-full -translate-x-32 -translate-y-32 opacity-30" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-green-100 rounded-full translate-x-32 translate-y-32 opacity-30" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">
            How It Works 🎄✨
          </h2>
          <p className="text-xl text-gray-600">
            See the magic of transformation! Create your unique holiday video in just three simple steps.
          </p>
        </motion.div>

        {/* Steps Grid */}
        <div className="space-y-8">
          {/* Step 1: Upload */}
          <StepCard
            step={1}
            title="Upload Your Video 📹"
            description="Record a short video (up to 30 seconds) of yourself or someone else. Be festive and have fun! Upload it to our platform using our simple drag-and-drop tool."
            icon={Upload}
          >
            <VideoComparison
              beforeVideo="/videos/sample-original.mp4"
              afterVideo="/videos/sample-transformed.mp4"
            />
          </StepCard>

          {/* Step 2: Customize */}
          <StepCard
            step={2}
            title="Customize Your Character 🎅✨"
            description="Personalize your message by adding a name, a heartfelt greeting, and selecting from our unique character styles. Make it truly yours!"
            icon={Wand2}
          >
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-6">
              {characters.map((character) => (
                <CharacterOption
                  key={character.id}
                  name={character.name}
                  image={character.image}
                  onClick={() => console.log(`Selected ${character.name}`)}
                />
              ))}
            </div>
          </StepCard>

          {/* Step 3: Share */}
          <StepCard
            step={3}
            title="Download and Share 🎁"
            description="Once your video is transformed, it will be ready for download within 24-48 hours. Share the magic with family, friends, or colleagues and make this Christmas unforgettable!"
            icon={Share2}
          >
            <div className="mt-6 bg-red-50 rounded-xl p-6 text-center">
              <p className="text-lg font-medium text-red-800 mb-4">
                Ready to create your magical holiday video? 🎅✨
              </p>
              <Button
                variant="primary"
                size="lg"
                onClick={() => document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' })}
                className="shadow-xl shadow-red-200/50"
              >
                Start Creating Now!
              </Button>
            </div>
          </StepCard>
        </div>
      </div>
    </section>
  );
};